(function () {
  window.upcartOpenOnAddToCartOverride = false;
  window.skipMultiAddButtonOpenDrawer = true;
  window.preventTimberDrawerOpen = true;
  const ELEMENT_NAME = "member-step-gift-cart";

  class MemberStepGiftCart extends HTMLElement {
    constructor() {
      super();
      this.cart = null;
      this.isLoading = false;
      this.memberStepGiftRetryCount = 0;
      this.memberStepGiftRetryTimer = null;
      this.unsubscribeCartReady = null;
      this.unsubscribeCartItemsChange = null;
      this.pendingRenderOptions = null;
      this.handleClick = this.handleClick.bind(this);
      this.handleCartUpdate = this.handleCartUpdate.bind(this);
    }

    async connectedCallback() {
      if (this.isConnectedInitialized) return;

      this.isConnectedInitialized = true;
      this.cacheElements();
      this.addEventListener("click", this.handleClick);
      this.bindCartEvents();
      await this.refresh({ forceFetch: true });
    }

    disconnectedCallback() {
      this.removeEventListener("click", this.handleClick);

      if (typeof this.unsubscribeCartReady === "function") {
        this.unsubscribeCartReady();
      }

      if (typeof this.unsubscribeCartItemsChange === "function") {
        this.unsubscribeCartItemsChange();
      }

      if (this.memberStepGiftRetryTimer) {
        clearTimeout(this.memberStepGiftRetryTimer);
      }

      this.unlockPageScroll();
    }

    cacheElements() {
      this.elements = {
        topbar: this.querySelector(".member-step-gift-cart__topbar"),
        topbarText: this.querySelector(".member-step-gift-cart__topbar-text"),
        mask: this.querySelector("[data-member-step-gift-cart-mask]"),
        subtotal: this.querySelector("[data-member-step-gift-cart-subtotal]"),
        save: this.querySelector("[data-member-step-gift-cart-save]"),
        compare: this.querySelector("[data-member-step-gift-cart-compare]"),
        progressBar: this.querySelector("[data-member-step-gift-cart-progress-bar]"),
        milestones: this.querySelector("[data-member-step-gift-cart-milestones]"),
        items: this.querySelector("[data-member-step-gift-cart-items]"),
        loading: this.querySelector("[data-member-step-gift-cart-loading]"),
      };
    }

    bindCartEvents() {
      if (!window.CartApi) return;

      if (typeof window.CartApi.onCartReady === "function") {
        this.unsubscribeCartReady = window.CartApi.onCartReady(this.handleCartUpdate);
      }

      if (typeof window.CartApi.onCartItemsObserve === "function") {
        this.unsubscribeCartItemsChange = window.CartApi.onCartItemsObserve(this.handleCartUpdate);
      }
    }

    handleCartUpdate({ cart } = {}) {
      // 移动端真机加购后touchmove被preventDefault导致页面不能滑动问题
      setTimeout(() => {
        if (window.timber?.RightDrawer?.drawerIsOpen) {
          window.timber.RightDrawer.drawerIsOpen = false;
        }
      }, 1000);
      this.renderCartUpdate({
        cart,
        forceFetch: !this.isValidCart(cart),
      }).catch((error) => {
        console.error("Member step gift cart failed to handle cart update:", error);
      });
    }

    async refresh(options = {}) {
      return this.renderCartUpdate(options);
    }

    async renderCartUpdate(options = {}) {
      if (this.isLoading) {
        this.pendingRenderOptions = {
          ...(this.pendingRenderOptions || {}),
          ...options,
          cart: null,
          forceFetch: true,
        };
        return;
      }

      this.isLoading = true;
      this.setLoading(true);

      try {
        const cart = await this.getCart(options);
        this.cart = cart;
        await this.ensureCompareAtPriceCache(cart);
        this.renderFromCart(cart);
      } catch (error) {
        console.error("Member step gift cart failed to refresh:", error);
        this.renderEmptyState();
      } finally {
        this.isLoading = false;
        this.setLoading(false);

        if (this.pendingRenderOptions) {
          const nextOptions = this.pendingRenderOptions;
          this.pendingRenderOptions = null;
          this.renderCartUpdate(nextOptions).catch((error) => {
            console.error("Member step gift cart failed to render pending update:", error);
          });
        }
      }
    }

    async ensureCompareAtPriceCache(cart) {
      const memberStepGift = window.MemberStepGift;

      if (!memberStepGift || typeof memberStepGift.ensureCompareAtPriceCache !== "function") return;

      try {
        await memberStepGift.ensureCompareAtPriceCache(cart);
      } catch (error) {
        console.error("Member step gift cart compare-at cache failed:", error);
      }
    }

    async getCart(options = {}) {
      if (!options.forceFetch && this.isValidCart(options.cart)) {
        return options.cart;
      }

      if (window.CartApi && typeof window.CartApi.getCart === "function") {
        return window.CartApi.getCart({ forceFetch: true });
      }

      const response = await fetch("/cart.js?_c_=t", {
        headers: {
          Accept: "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(`Cart fetch failed: ${response.status}`);
      }

      return response.json();
    }

    renderFromCart(cart) {
      const state = this.getMemberStepGiftState(cart);

      if (!state) {
        this.scheduleMemberStepGiftRetry();
        this.renderEmptyState();
        return;
      }

      this.memberStepGiftRetryCount = 0;

      const displayItems = this.splitItemsByQuantity(state.eligibleItems);

      this.dataset.hasConfig = state.hasConfig ? "true" : "false";
      this.dataset.memberQualified = state.isQualifiedCustomer ? "true" : "false";
      this.dataset.eligibleItemCount = String(displayItems.length);
      this.dataset.unlockedGiftCount = String(state.unlockedGiftCount);
      this.dataset.memberStepGiftEligibleSubtotal = String(state.thresholdPrice);

      this.renderTopbarText(state);
      this.renderSummary(state);
      this.renderProgress(state);
      this.renderItems(displayItems);
    }

    getMemberStepGiftState(cart) {
      if (window.MemberStepGift && typeof window.MemberStepGift.getCartState === "function") {
        return window.MemberStepGift.getCartState(cart);
      }

      return null;
    }

    scheduleMemberStepGiftRetry() {
      if (this.memberStepGiftRetryCount >= 10 || this.memberStepGiftRetryTimer) return;

      this.memberStepGiftRetryCount += 1;
      this.memberStepGiftRetryTimer = setTimeout(() => {
        this.memberStepGiftRetryTimer = null;
        this.refresh({
          cart: this.cart,
          forceFetch: !this.isValidCart(this.cart),
        });
      }, 100);
    }

    renderSummary(state) {
      if (this.elements.subtotal) {
        this.elements.subtotal.textContent = this.formatMoney(state.subtotal, true);
      }

      if (this.elements.save) {
        this.elements.save.textContent = this.formatMoney(state.savePrice);
      }

      if (this.elements.compare) {
        this.elements.compare.textContent =
          state.originalSubtotal > state.subtotal ? this.formatMoney(state.originalSubtotal, true) : "";
      }
    }

    renderTopbarText(state) {
      if (!this.elements.topbarText) return;

      const giftRules = Array.isArray(state.giftRules)
        ? state.giftRules
            .slice()
            .filter((gift) => Number.isFinite(Number(gift.minimumPrice)))
            .sort((a, b) => Number(a.minimumPrice) - Number(b.minimumPrice))
        : [];

      this.elements.topbarText.innerHTML = "";

      if (giftRules.length === 0) return;

      const thresholdPrice = Number(state.thresholdPrice || 0);
      const unlockedRules = giftRules.filter((gift) => thresholdPrice >= Number(gift.minimumPrice || 0));
      const currentRule = unlockedRules[unlockedRules.length - 1] || null;
      const nextRule = giftRules.find((gift) => thresholdPrice < Number(gift.minimumPrice || 0));

      if (currentRule) {
        this.elements.topbarText.appendChild(this.createTopbarTextLine(`${this.getRewardTitle(currentRule)} Eligible`));
      }

      if (nextRule) {
        const gapPrice = Math.max(Number(nextRule.minimumPrice || 0) - thresholdPrice, 0);
        this.elements.topbarText.appendChild(
          this.createTopbarTextLine(
            `You're still ${this.formatMoney(gapPrice)} away from ${this.getRewardTitle(nextRule)}`,
          ),
        );
      }
    }

    createTopbarTextLine(text) {
      const line = document.createElement("div");
      line.className = "member-step-gift-cart__topbar-text-line";
      line.textContent = text;

      return line;
    }

    getRewardTitle(gift) {
      return gift?.rewardTitle || gift?.title || gift?.label || gift?.giftHandle || "";
    }

    setLoading(isLoading) {
      this.toggleAttribute("loading", isLoading);
      this.setAttribute("aria-busy", isLoading ? "true" : "false");

      if (!this.elements?.loading) return;

      this.elements.loading.style.display = isLoading ? "" : "none";
      this.elements.loading.setAttribute("aria-hidden", isLoading ? "false" : "true");
    }

    renderProgress(state) {
      if (this.elements.progressBar) {
        this.elements.progressBar.style.width = `${state.progressPercent}%`;
      }

      if (!this.elements.milestones) return;

      this.elements.milestones.innerHTML = "";
      this.elements.milestones.appendChild(
        this.createMilestone({
          label: "$0",
          position: 0,
        }),
      );

      const maxGiftMinimumPrice =
        state.giftRules.length > 0 ? Math.max(...state.giftRules.map((gift) => gift.minimumPrice)) : 0;

      state.giftRules.forEach((gift) => {
        this.elements.milestones.appendChild(
          this.createMilestone({
            label: gift.rewardTitle || gift.title || gift.label || gift.giftHandle || "",
            position: maxGiftMinimumPrice > 0 ? Math.min((gift.minimumPrice / maxGiftMinimumPrice) * 100, 100) : 0,
            threshold: gift.threshold,
            variantId: gift.variantId,
            giftHandle: gift.giftHandle,
            unlocked: state.thresholdPrice >= gift.minimumPrice && state.isQualifiedCustomer,
          }),
        );
      });
    }

    createMilestone(milestone) {
      const item = document.createElement("div");
      item.className = "member-step-gift-cart__milestone";
      item.dataset.position = String(milestone.position);
      item.dataset.unlocked = milestone.unlocked ? "true" : "false";
      item.style.left = `${milestone.position}%`;

      if (milestone.threshold || milestone.threshold === 0) {
        item.dataset.threshold = String(milestone.threshold);
      }

      if (milestone.variantId) {
        item.dataset.variantId = String(milestone.variantId);
      }

      if (milestone.giftHandle) {
        item.dataset.giftHandle = milestone.giftHandle;
      }

      const marker = document.createElement("span");
      marker.className = "member-step-gift-cart__milestone-marker";
      marker.setAttribute("aria-hidden", "true");
      marker.innerHTML =
        '<svg xmlns="http://www.w3.org/2000/svg" width="7" height="6" viewBox="0 0 7 6" fill="none"><path d="M3.03101 5.25L-8.34286e-05 5.70966e-07L6.06209 4.09935e-08L3.03101 5.25Z" fill="currentColor"/></svg>';

      const label = document.createElement("span");
      label.className = "member-step-gift-cart__milestone-label";
      label.textContent = milestone.label || "";

      item.append(marker, label);
      return item;
    }

    renderItems(items) {
      if (!this.elements.items) return;

      this.elements.items.innerHTML = "";

      items.forEach((item) => {
        const row = document.createElement("div");
        row.className = "member-step-gift-cart__item";
        row.setAttribute("role", "listitem");
        row.dataset.cartItemKey = item.key || "";
        row.dataset.cartItemQuantity = String(item.quantity || 1);
        row.dataset.variantId = String(item.variant_id || item.variantId || "");
        row.dataset.productHandle = item.handle || "";

        const title = document.createElement("span");
        title.className = "member-step-gift-cart__item-title";
        title.textContent = item.product_title || item.productTitle || item.title || "";

        const remove = document.createElement("button");
        remove.className = "member-step-gift-cart__item-remove";
        remove.type = "button";
        remove.dataset.memberStepGiftCartRemove = "";
        remove.dataset.cartItemKey = item.key || "";
        remove.dataset.cartItemQuantity = String(item.quantity || 1);
        remove.setAttribute("aria-label", `Remove ${title.textContent}`);
        remove.innerHTML =
          '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">\n  <path d="M7.91699 0C12.2892 2.57701e-07 15.834 3.54474 15.834 7.91699C15.834 12.2892 12.2892 15.834 7.91699 15.834C3.54474 15.834 2.57692e-07 12.2892 0 7.91699C0 3.54474 3.54474 0 7.91699 0ZM7.91699 0.990234C4.09127 0.990234 0.990234 4.09127 0.990234 7.91699C0.990235 11.7427 4.09127 14.8438 7.91699 14.8438C11.7427 14.8437 14.8437 11.7427 14.8438 7.91699C14.8438 4.09127 11.7427 0.990235 7.91699 0.990234ZM10.7334 4.40039C10.9266 4.20716 11.2404 4.20716 11.4336 4.40039C11.6268 4.59362 11.6268 4.90736 11.4336 5.10059L8.61719 7.91699L11.4336 10.7334C11.6268 10.9266 11.6268 11.2404 11.4336 11.4336C11.2404 11.6268 10.9266 11.6268 10.7334 11.4336L7.91699 8.61719L5.10059 11.4336C4.90736 11.6268 4.59362 11.6268 4.40039 11.4336C4.20716 11.2404 4.20716 10.9266 4.40039 10.7334L7.2168 7.91699L4.40039 5.10059C4.20716 4.90736 4.20716 4.59362 4.40039 4.40039C4.59362 4.20716 4.90736 4.20716 5.10059 4.40039L7.91699 7.2168L10.7334 4.40039Z" fill="#918686"/>\n</svg>';

        row.append(title, remove);
        this.elements.items.appendChild(row);
      });
    }

    splitItemsByQuantity(items) {
      if (!Array.isArray(items)) return [];

      return items.flatMap((item) => {
        const quantity = Math.max(1, Number(item.quantity || 1));

        return Array.from({ length: quantity }, () => item);
      });
    }

    renderEmptyState() {
      this.dataset.hasConfig = "false";
      this.dataset.memberQualified = "false";
      this.dataset.eligibleItemCount = "0";
      this.dataset.unlockedGiftCount = "0";
      this.dataset.memberStepGiftEligibleSubtotal = "0";

      if (this.elements.topbarText) {
        this.elements.topbarText.innerHTML = "";
      }

      if (this.elements.subtotal) {
        this.elements.subtotal.textContent = this.formatMoney(0, true);
      }

      if (this.elements.save) {
        this.elements.save.textContent = this.formatMoney(0);
      }

      if (this.elements.compare) {
        this.elements.compare.textContent = "";
      }

      if (this.elements.progressBar) {
        this.elements.progressBar.style.width = "0%";
      }

      if (this.elements.milestones) {
        this.elements.milestones.innerHTML = "";
        this.elements.milestones.appendChild(
          this.createMilestone({
            label: "$0",
            position: 0,
          }),
        );
      }

      if (this.elements.items) {
        this.elements.items.innerHTML = "";
      }
    }

    async handleClick(event) {
      const buyNowButton = event.target.closest(".member-step-gift-cart__button--buy");

      if (buyNowButton && this.contains(buyNowButton)) {
        window.location.href = "/checkout";
        return;
      }

      const removeButton = event.target.closest("[data-member-step-gift-cart-remove]");

      if (removeButton && this.contains(removeButton)) {
        const key = removeButton.dataset.cartItemKey;
        if (!key) return;

        const quantity = Math.max(1, Number(removeButton.dataset.cartItemQuantity || 1));
        removeButton.disabled = true;
        this.setLoading(true);

        try {
          await this.removeCartItem(key, quantity);
          await this.refresh({ forceFetch: true });
        } catch (error) {
          console.error("Member step gift cart failed to remove item:", error);
          removeButton.disabled = false;
          this.setLoading(false);
        }

        return;
      }

      this.handleMobileExpandCollapseClick(event);
    }

    handleMobileExpandCollapseClick(event) {
      if (!this.isMobileExpandCollapseEnabled()) return false;

      const actions = event.target.closest(".member-step-gift-cart__actions");
      const items = event.target.closest(".member-step-gift-cart__items");
      const isInActions = !!(actions && this.contains(actions));
      const isInItems = !!(items && this.contains(items));

      if (this.classList.contains("expand")) {
        if (!isInActions && !isInItems) {
          this.collapse();
          return true;
        }

        return false;
      }

      if (!isInActions) {
        this.expand();
        return true;
      }

      return false;
    }

    isMobileExpandCollapseEnabled() {
      return window.innerWidth < 1440;
    }

    expand() {
      this.classList.add("expand");
      this.lockPageScroll();

      if (this.elements.mask) {
        this.elements.mask.setAttribute("aria-hidden", "false");
      }
    }

    collapse() {
      this.classList.remove("expand");
      this.unlockPageScroll();

      if (this.elements.mask) {
        this.elements.mask.setAttribute("aria-hidden", "true");
      }
    }

    lockPageScroll() {
      if (this.isPageScrollLocked) return;

      this.previousBodyOverflow = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      this.isPageScrollLocked = true;
    }

    unlockPageScroll() {
      if (!this.isPageScrollLocked) return;

      document.body.style.overflow = this.previousBodyOverflow || "";
      this.previousBodyOverflow = "";
      this.isPageScrollLocked = false;
    }

    async removeCartItem(key, quantity) {
      const nextQuantity = Math.max(0, Number(quantity || 1) - 1);

      if (window.CartApi && typeof window.CartApi.updateItems === "function") {
        await window.CartApi.updateItems([{ key, quantity: nextQuantity }], {
          source: "member_step_gift_cart_remove",
        });

        if (typeof window.CartApi.refreshCart === "function") {
          await window.CartApi.refreshCart({
            source: "member_step_gift_cart_remove",
          });
        }

        return;
      }

      const response = await fetch("/cart/change.js?_c_=t", {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          id: key,
          quantity: nextQuantity,
        }),
      });

      if (!response.ok) {
        throw new Error(`Cart item remove failed: ${response.status}`);
      }
    }

    formatMoney(cents, withCurrency = false) {
      const amount = Number(cents || 0);

      if (window.theme?.Currency && typeof window.theme.Currency.formatMoney === "function") {
        const format = withCurrency
          ? window.theme?.strings?.moneyWithCurrencyFormat || window.theme?.strings?.moneyFormat
          : window.theme?.strings?.moneyFormat;

        return window.theme.Currency.formatMoney(amount, format);
      }

      if (window.Shopify && typeof window.Shopify.formatMoney === "function") {
        return window.Shopify.formatMoney(amount);
      }

      const currency = window.Shopify?.currency?.active || this.cart?.currency || "USD";

      try {
        return new Intl.NumberFormat(undefined, {
          style: "currency",
          currency,
        }).format(amount / 100);
      } catch (error) {
        return `$${(amount / 100).toFixed(2)}`;
      }
    }

    isValidCart(cart) {
      return !!cart && Array.isArray(cart.items);
    }
  }

  if (!customElements.get(ELEMENT_NAME)) {
    customElements.define(ELEMENT_NAME, MemberStepGiftCart);
  }
})();
